import React, { useState, useEffect, useRef } from 'react';
import { renderMarkdown } from './markdownRenderer';
import PlatformFilePicker from './PlatformFilePicker';

const GOLD = '#D4A417';
const PANEL_BG = '#1e2235';
const SURFACE = '#13161e';
const BORDER = '#252a38';
const API_BASE = 'https://apispeccheck.zonetap.com';

// Filename pattern that marks a PDF as a drawing (routed to the drawings field, not specs).
const DRAWING_NAME_RE = /\.dwg$|drawing[s]?\s*\d|floor\s*plan|site\s*plan|elevation\s*dwg|sheet\s*[a-z]\d|\bA\d{3}\b|\bM\d{3}\b|\bE\d{3}\b|\bP\d{3}\b|\bS\d{3}\b/i;

const TOOL_OPTIONS = {
  'spec-update': {
    question: 'How would you like to run the Spec Update?',
    options: [
      { label: '🔍 Live Manufacturer Search', desc: 'Search active manufacturer databases for updates', value: { liveCheck: true } },
      { label: '📄 Spec Analysis Only', desc: 'Analyze spec without live web search', value: { liveCheck: false } },
    ]
  },
  'shop-drawing': {
    question: 'Which trade discipline should I focus on?',
    options: [
      { label: '🌡️ Mechanical (HVAC)', desc: 'Focus on mechanical systems', value: { focusAreas: 'Mechanical,HVAC' } },
      { label: '⚡ Electrical', desc: 'Focus on electrical systems', value: { focusAreas: 'Electrical' } },
      { label: '🚰 Plumbing', desc: 'Focus on plumbing systems', value: { focusAreas: 'Plumbing' } },
      { label: '🔥 Fire Protection', desc: 'Focus on fire protection', value: { focusAreas: 'Fire Protection' } },
      { label: '🏗️ Structural', desc: 'Focus on structural elements', value: { focusAreas: 'Structural' } },
      { label: '📋 All Trades', desc: 'Comprehensive review of all trades', value: { focusAreas: '' } },
    ],
    followUp: {
      question: 'Select priority focus areas (or skip for full review):',
      multiSelect: true,
      options: [
        { label: '📐 Dimensions & Measurements', value: 'Dimensions' },
        { label: '🧱 Materials & Finishes', value: 'Materials' },
        { label: '📝 Annotations & Notes', value: 'Annotations' },
        { label: '📋 Code & Standards Compliance', value: 'Code' },
        { label: '🔗 Trade Coordination', value: 'Coordination' },
        { label: '🔧 Product & Equipment Schedule', value: 'Equipment' },
        { label: '🔩 Mounting & Support Details', value: 'Mounting' },
        { label: '🔌 Connection Details', value: 'Connections' },
      ]
    }
  },
  'drawing-compare': {
    question: 'Which trade discipline are these drawings?',
    options: [
      { label: '🌡️ Mechanical (HVAC)', desc: 'Mechanical/HVAC drawings', value: { focusAreas: 'Mechanical,HVAC' } },
      { label: '⚡ Electrical', desc: 'Electrical drawings', value: { focusAreas: 'Electrical' } },
      { label: '🚰 Plumbing', desc: 'Plumbing drawings', value: { focusAreas: 'Plumbing' } },
      { label: '🏗️ Structural', desc: 'Structural drawings', value: { focusAreas: 'Structural' } },
      { label: '📐 Architectural', desc: 'Architectural drawings', value: { focusAreas: 'Architectural' } },
      { label: '📋 General', desc: 'General/multi-discipline', value: { focusAreas: '' } },
    ]
  },
'design-vs-shop': {
  question: 'Which trade discipline are these drawings?',
  options: [
    { label: '🌡️ Mechanical (HVAC)', desc: 'Focus on mechanical systems', value: { trade: 'Mechanical,HVAC', designCount: 1 } },
    { label: '⚡ Electrical', desc: 'Focus on electrical systems', value: { trade: 'Electrical', designCount: 1 } },
    { label: '🚰 Plumbing', desc: 'Focus on plumbing systems', value: { trade: 'Plumbing', designCount: 1 } },
    { label: '🔥 Fire Protection', desc: 'Focus on fire protection', value: { trade: 'Fire Protection', designCount: 1 } },
    { label: '🏗️ Structural', desc: 'Focus on structural elements', value: { trade: 'Structural', designCount: 1 } },
    { label: '📐 Architectural', desc: 'Architectural drawings', value: { trade: 'Architectural', designCount: 1 } },
    { label: '📋 General / Multi-trade', desc: 'General review across all trades', value: { trade: '', designCount: 1 } },
  ]
},
};

// Registry of runnable tools — maps a tool id to its display label and endpoint.
const TOOL_REGISTRY = {
  'spec-check':      { label: 'Spec Check',      endpoint: '/api/upload/' },
  'spec-update':     { label: 'Spec Update',      endpoint: '/api/upload/spec-update' },
  'spec-builder':    { label: 'Spec Builder',     endpoint: '/api/upload/spec-builder' },
  'rfi-check':       { label: 'RFI Check',        endpoint: '/api/upload/rfi-check' },
  'shop-drawing':    { label: 'Package Check',    endpoint: '/api/shop-drawing-check' },
  'drawing-compare': { label: 'Drawing Compare',  endpoint: '/api/drawing-compare/analyze' },
  'house-rules':     { label: 'House Rules',      endpoint: '/api/upload/house-rules' },
  'draft-rfi':       { label: 'Draft RFIs',       endpoint: '/api/manager-tools/draft-rfi' },
  'submittal-review-letter': { label: 'Submittal Review Letter', endpoint: '/api/manager-tools/submittal-review-letter' },
  'design-vs-shop': { label: 'Design vs. Shop', endpoint: '/api/design-vs-shop/analyze' },
  'track-changes': { label: 'Track Changes', endpoint: '/api/upload/track-changes-compare' },
};

// Per-tool prompts asking the user to attach the right files before a chained run.
// No 'house-rules' entry — it runs automatically and never appears as a chain option.
const TOOL_FILE_HINTS = {
  'rfi-check':       'Please attach your RFI document. I\'ll use your existing spec files as project reference.',
  'shop-drawing':    'Please attach your shop drawing files. I\'ll cross-reference against your existing spec.',
  'spec-update':     'Please attach the spec sheet you want to update.',
  'spec-builder':    'Please attach a master spec and template file.',
  'drawing-compare': 'Please attach two drawing versions to compare.',
};

// Maps each tool's selected files onto the field names its endpoint expects.
const fieldMap = {
'spec-check':      (files, fd, _, optionValues) => {
    const submittalName = optionValues?.submittalName;
    const images = files.filter(f => f.type?.startsWith('image/'));
    const allPdfs = files.filter(f => !f.type?.startsWith('image/'));
    const pdfDrawings = allPdfs.filter(f => DRAWING_NAME_RE.test(f.name) && f.name !== submittalName);
    const specPdfs = allPdfs.filter(f => !DRAWING_NAME_RE.test(f.name) || f.name === submittalName);

    images.forEach(f => fd.append('drawings', f));
    pdfDrawings.forEach(f => fd.append('drawings', f));

    if (submittalName) {
      specPdfs.forEach(f => {
        if (f.name === submittalName) fd.append('submittal', f);
        else fd.append('specs', f);
      });
    } else if (specPdfs.length === 1) {
      fd.append('specs', specPdfs[0]);
    } else if (specPdfs.length >= 2) {
      specPdfs.slice(0, -1).forEach(f => fd.append('specs', f));
      fd.append('submittal', specPdfs[specPdfs.length - 1]);
    }
  },
  'spec-update':     (files, fd) => {
    if (files[0]) fd.append('specSheet', files[0]);
  },
  'spec-builder':    (files, fd) => {
    if (files[0]) fd.append('masterSpec', files[0]);
    if (files[1]) fd.append('template', files[1]);
  },
  'rfi-check':       (files, fd, cachedSpecFiles) => {
    // First file is the new RFI doc, spec files provide project context
    if (files[0]) fd.append('rfi', files[0]);
    const projectDocs = files[1] || (cachedSpecFiles && cachedSpecFiles[0]);
    if (projectDocs) fd.append('projectDocs', projectDocs);
  },
  'shop-drawing':    (files, fd, cachedSpecFiles) => {
    // New files are drawings, cached spec provides reference
    files.forEach(f => fd.append('drawings', f));
    if (cachedSpecFiles && cachedSpecFiles[0]) fd.append('specs', cachedSpecFiles[0]);
  },
'design-vs-shop': (files, fd, _, optionValues) => {
  const count = optionValues?.designCount;
  const designCount = count === 'half' ? Math.floor(files.length / 2) : (parseInt(count) || 1);
  files.slice(0, designCount).forEach(f => fd.append('designDrawings', f));
  files.slice(designCount).forEach(f => fd.append('shopDrawings', f));
  if (optionValues?.trade) fd.append('trade', String(optionValues.trade));
},

  'drawing-compare': (files, fd, _, optionValues) => {
    if (files[0]) fd.append('baselineDrawing', files[0]);
    if (files[1]) fd.append('newDrawing', files[1]);
    if (optionValues?.customFocus) fd.append('customFocus', optionValues.customFocus);
  },
  'track-changes': (files, fd) => {
  if (files[0]) fd.append('masterSpec', files[0]);
  if (files[1]) fd.append('customerSpec', files[1]);
},
  'house-rules':     (files, fd) => {
    if (files[0]) fd.append('document', files[0]);
  },
  'draft-rfi':       (files, fd) => {
    // No files needed — uses lastResultSummary report text
  },
  'submittal-review-letter': (files, fd) => {
    // No files needed — uses lastResultSummary report text
  },
};

async function downloadDocx(reportText, toolLabel) {
  try {
    const res = await fetch(`${API_BASE}/api/manager-tools/export-docx`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ report: reportText, toolLabel })
    });
    const data = await res.json();
    if (!data.docxBase64) return;
    const byteChars = atob(data.docxBase64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = data.filename || `Report_${new Date().toISOString().slice(0,10)}.docx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Word export error:', err);
  }
}

// Download the backend-generated branded PDF when available; otherwise fall back to plain text.
async function downloadCombinedReport(chainResults) {
  try {
    const res = await fetch(`${API_BASE}/api/manager-tools/combined-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        reports: chainResults.map(r => ({
          toolLabel: r.toolLabel,
          report: r.report
        })),
        userId: ''
      })
    });
    const data = await res.json();
    if (!data.pdfBase64) throw new Error('No PDF returned');

    const byteChars = atob(data.pdfBase64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = data.filename || `Combined_Report_${new Date().toISOString().slice(0,10)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Combined report error:', err);
    // Fallback to text download
    const combined = chainResults.map(r =>
      `${'='.repeat(60)}\n${r.toolLabel.toUpperCase()}\n${'='.repeat(60)}\n\n${r.report}`
    ).join('\n\n');
    const blob = new Blob([combined], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Combined_Report_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

function downloadReport(pdfBase64, text, toolLabel, reportFilename) {
  const filename = reportFilename || `${toolLabel.replace(/\s+/g, '-')}-Report-${new Date().toISOString().slice(0,10)}.pdf`;
  if (pdfBase64) {
    // Use backend-generated branded PDF
    const byteChars = atob(pdfBase64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } else {
    // Fallback: plain text download (keep a .txt extension so the blob isn't mislabeled)
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${toolLabel.replace(/\s+/g, '-')}-Report-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// Maps an activeTab key to its human-readable tool name (mirrors TopBar).
const TAB_LABELS = {
  'spec-check': 'Spec Check',
  'spec-builder': 'Spec Builder',
  'spec-update': 'Spec Update',
  'rfi-check': 'RFI Check',
  'shop-drawing-check': 'Package Check',
  'design-vs-shop': 'Design vs. Shop',
  'drawing-compare': 'Drawing Compare',
  'track-changes': 'Track Changes',
  'general': 'General Consultation',
  'integrations': 'Integrations',
  'settings': 'Settings',
  'faq': 'Help & FAQ',
};

// Spec/RFI docs get a green dot, drawing docs get a blue dot.
function dotColorForDocType(docType) {
  const t = (docType || '').toLowerCase();
  if (t.includes('drawing')) return '#3b82f6'; // blue
  return '#22c55e'; // green
}

function truncate(name, max) {
  const n = name || '';
  if (n.length <= max) return n;
  return n.slice(0, max - 1) + '…';
}

function buildInitialMessages(activeTab, cachedDocs, user) {
  const toolLabel = TAB_LABELS[activeTab] || 'this tool';
  const docs = Array.isArray(cachedDocs) ? cachedDocs : [];

  const emailLocal = (user?.email || '').split('@')[0];
  const fullName = user?.name && !user.name.includes('@') ? user.name : null;
  const firstName = user?.given_name
    || (fullName ? fullName.split(' ')[0] : null)
    || (emailLocal.includes('.') ? emailLocal.split('.')[0] : null)
    || (emailLocal.length <= 12 ? emailLocal : null)
    || null;
  const displayName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
    : null;
  const greeting = displayName ? `Hi ${displayName}` : 'Hi';

  let message;
if (docs.length > 0) {
  const names = docs.slice(0, 2).map(d => d.name).join(', ');
  const more = docs.length > 2 ? ` and ${docs.length - 2} more` : '';
  message = `${greeting} — I can see you have **${docs.length} cached document${docs.length > 1 ? 's' : ''}** (${names}${more}). Tell me which to use as specs and which is the submittal, or attach new documents to review.`;
  } else {
    message = `${greeting} — I can see you're on ${toolLabel}. Attach your documents and tell me what you'd like to review.`;
  }

  return [{ role: 'agent', text: message }];
}

function getChainLimit(tier) {
  const t = (tier || '').toLowerCase();
  if (t === 'enterprise') return Infinity;
  if (t === 'pro') return 3;
  return 0; // Standard/Trial — no chaining
}

function ManagerSidebar({ activeTab, user, cachedDocs, onResult, lastResultSummary, tier, vaultDrawings, inputPrefill, onInputPrefillUsed, onVaultChange, onCacheChange, onLoadingChange, width, resizable, onWidthChange }) {
  const sidebarWidth = width || 360;
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const newWidth = Math.min(700, Math.max(320, window.innerWidth - e.clientX));
      onWidthChange && onWidthChange(newWidth);
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onWidthChange]);  
  const docs = Array.isArray(cachedDocs) ? cachedDocs : [];
  const cachedDocsKey = docs.map(d => d.name).join(',');
  const [messages, setMessages] = useState(() => buildInitialMessages(activeTab, cachedDocs, user));
  const [input, setInput] = useState('');
  const [selected, setSelected] = useState([]); // selected chain option labels
  const [loading, setLoading] = useState(false);
  useEffect(() => { onLoadingChange?.(loading); }, [loading]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fetchingDocs, setFetchingDocs] = useState(new Set());
  const [showPlatformPicker, setShowPlatformPicker] = useState(false);
  const [pendingToolExecution, setPendingToolExecution] = useState(null);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [pendingSpecBuilder, setPendingSpecBuilder] = useState(null);
  const threadRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastUsedFilesRef = useRef([]);
  const lastUsedSpecFilesRef = useRef([]);
  const attachedFilesRef = useRef([]);
  const lastReportRef = useRef(null);
  const chainResultsRef = useRef([]);

  // Mirror attachedFiles into a ref so async callbacks read the latest value.
  useEffect(() => {
    attachedFilesRef.current = attachedFiles;
  }, [attachedFiles]);

  // Reset the greeting when the active tool changes.
  useEffect(() => {
    setMessages(buildInitialMessages(activeTab, cachedDocs, user));
    setSelected([]);
  }, [activeTab, cachedDocsKey]);

  // Keep the thread scrolled to the latest message.
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (inputPrefill) {
      setInput(inputPrefill);
      onInputPrefillUsed?.();
    }
  }, [inputPrefill]);

  const clearMessages = () => {
    setMessages(buildInitialMessages(activeTab, cachedDocs, user));
    setSelected([]);
  };

  const toggleOption = (label) => {
    setSelected((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  // Fetch a cached document's bytes and attach it as a File the tools can upload.
  const fetchAndAttachCachedDoc = async (doc) => {
    // Block if already fetching or already attached
    if (fetchingDocs.has(doc.id) || attachedFilesRef.current.some(f => f.name === doc.name)) return;
    setFetchingDocs(prev => new Set([...prev, doc.id]));
    try {
      // Stream the raw file from the download endpoint and wrap it as a File.
      const res = await fetch(
        `${API_BASE}/api/documents/${doc.id}/download?userId=${encodeURIComponent(user?.email || '')}&t=${Date.now()}`,
        { cache: 'no-store' }
      );
      if (!res.ok) {
        console.error('Failed to download cached doc:', res.status);
        return;
      }
      const blob = await res.blob();
      const file = new File([blob], doc.name, { type: doc.mimeType || 'application/pdf' });

      setAttachedFiles(prev => {
        const next = [...prev, file];
        attachedFilesRef.current = next;
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch cached doc:', err);
    } finally {
      setFetchingDocs(prev => { const next = new Set(prev); next.delete(doc.id); return next; });
    }
  };

  const fetchAndAttachVaultDrawing = async (drawing) => {
    if (attachedFilesRef.current.some(f => f.name === drawing.fileName)) return;
    setFetchingDocs(prev => new Set([...prev, drawing.id]));
    try {
      const res = await fetch(
        `${API_BASE}/api/drawing-compare/vault/${drawing.id}/download?userId=${encodeURIComponent(user?.email || '')}&t=${Date.now()}`,
        { cache: 'no-store' }
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const actualMimeType = blob.type && blob.type !== 'application/octet-stream'
        ? blob.type
        : (drawing.mimeType || 'application/pdf');
      const file = new File([blob], drawing.fileName, { type: actualMimeType });
      setAttachedFiles(prev => {
        const next = [...prev, file];
        attachedFilesRef.current = next;
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch vault drawing:', err);
    } finally {
      setFetchingDocs(prev => { const next = new Set(prev); next.delete(drawing.id); return next; });
    }
  };

  const runSelected = async () => {
    // If we have a pending drawing-compare with vault baseline and files just attached
if (pendingToolExecution?.toolId === 'spec-check' &&
    attachedFilesRef.current.length > 0) {
  const submittalFile = attachedFilesRef.current[0];
  const combinedFiles = [
    ...(pendingToolExecution.files || []),
    submittalFile
  ];
const savedChains = pendingToolExecution.chainSuggestions || [];
await executeToolWithOptions(
  pendingToolExecution.toolId,
  pendingToolExecution.tool,
  combinedFiles,
  { submittalName: submittalFile.name }
);
// Show chain suggestions manually since pendingToolExecution is about to be cleared
if (savedChains.length > 0) {
  setMessages(prev => [...prev, {
    role: 'agent',
    text: 'Would you like to continue with any of these?',
    chainOptions: savedChains.map(t => ({ icon: '🔧', label: t.label, desc: `Run ${t.label} on your documents` }))
  }]);
}
setPendingToolExecution(null);
  return;
}

// Track Changes — attach customer spec
if (pendingToolExecution?.toolId === 'track-changes' &&
    attachedFilesRef.current.length > 0) {
  const customerSpec = attachedFilesRef.current[0];
  const combinedFiles = [
    ...(pendingToolExecution.files || []),
    customerSpec
  ];
  await executeToolWithOptions(
    pendingToolExecution.toolId,
    pendingToolExecution.tool,
    combinedFiles,
    {}
  );
  setPendingToolExecution(null);
  return;
}
if (pendingToolExecution?.toolId === 'design-vs-shop' &&
    attachedFilesRef.current.length > 0 &&
    pendingToolExecution?.optionValues) {
  const combinedFiles = [
    ...(pendingToolExecution.files || []),
    ...attachedFilesRef.current
  ];
  await executeToolWithOptions(
    pendingToolExecution.toolId,
    pendingToolExecution.tool,
    combinedFiles,
    pendingToolExecution.optionValues
  );
  setPendingToolExecution(null);
  return;
}

if (pendingToolExecution?.toolId === 'drawing-compare' && 
    pendingToolExecution?.optionValues?.baselineType === 'upload' &&
    attachedFilesRef.current.length >= 1) {
  const combinedFiles = [
    ...(pendingToolExecution.files || []),
    ...attachedFilesRef.current
  ];
  // Capture any custom focus text the user may have typed
const customFocusText = input.trim();
if (customFocusText) setInput('');
const finalOptionValues = customFocusText
  ? { ...pendingToolExecution.optionValues, customFocus: customFocusText }
  : pendingToolExecution.optionValues;

if (combinedFiles.length >= 2 && combinedFiles[0].name === combinedFiles[1].name) {
    setMessages(prev => [...prev, {
      role: 'agent',
      text: '⚠️ The baseline and new drawing appear to be the same file. Please attach a different drawing to compare against.',
    }]);
    setAttachedFiles([]);
    attachedFilesRef.current = [];
    return;
  }

  await executeToolWithOptions(
    pendingToolExecution.toolId,
    pendingToolExecution.tool,
    combinedFiles,
    finalOptionValues
  );
  setPendingToolExecution(null);
  return;
}

if (selected.length === 0) return;

    // Find the tool registry entries for selected labels
    const toolsToRun = selected.map(label =>
      Object.entries(TOOL_REGISTRY).find(([, t]) => t.label === label)
    ).filter(Boolean);


    setLoading(true);

    // Append user confirmation message
    setMessages(prev => [...prev, {
      role: 'user',
      text: `Run: ${selected.join(', ')}`
    }]);

    // Add thinking placeholder
    const thinkingId = Date.now();
    setMessages(prev => [...prev, {
      role: 'agent',
      text: `Running ${selected.join(' → ')}... this may take a moment.`,
      pending: true,
      id: thinkingId
    }]);

    try {
      for (let i = 0; i < toolsToRun.length; i++) {
        const [toolId, tool] = toolsToRun[i];

        // Fall back to the files used at dispatch time if nothing is currently attached.
        const filesToUse = attachedFilesRef.current.length > 0 ? attachedFilesRef.current : lastUsedFilesRef.current;

        // If files came from a previous run (not freshly attached), warn user for tools that need specific file types
        const usingCachedFiles = attachedFilesRef.current.length === 0 && lastUsedFilesRef.current.length > 0;
        const hint = TOOL_FILE_HINTS[toolId];
        if (usingCachedFiles && hint && toolId !== 'draft-rfi' && toolId !== 'submittal-review-letter') {
          setMessages(prev => prev.map(m =>
            m.pending ? {
              ...m,
              text: `To run **${tool.label}**, ${hint}`,
              pending: false
            } : m
          ));
          setLoading(false);
          return;
        }

        // Spec Check needs 2+ non-drawing PDFs (spec + submittal) — drawings route separately
        const pdfFiles = filesToUse.filter(f => !f.type?.startsWith('image/'));
        const specPdfs = pdfFiles.filter(f => !DRAWING_NAME_RE.test(f.name));
        if (toolId === 'spec-check' && specPdfs.length < 2) {
          setMessages(prev => prev.map(m =>
            m.pending ? {
              ...m,
              text: 'I need both a specification PDF and a submittal PDF to run Spec Check. Images and drawing PDFs (arch, floor plan, etc.) attach as drawings automatically. Please attach at least 2 non-drawing PDF files and try again.',
              pending: false
            } : m
          ));
          return;
        }

        let data;

        // draft-rfi and submittal-review-letter send JSON (report text), all other tools send FormData (files)
        if (toolId === 'draft-rfi' || toolId === 'submittal-review-letter') {
          if (!lastReportRef.current?.fullReport) {
            setMessages(prev => prev.map(m =>
              m.pending ? {
                ...m,
                text: 'No previous report found. Please run a Spec Check first, then Draft RFIs.',
                pending: false
              } : m
            ));
            setLoading(false);
            return;
          }

          const res = await fetch(`${API_BASE}${tool.endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              report: lastReportRef.current.fullReport,
              userId: user?.email || '',
              projectName: 'AW Manager Project'
            })
          });
          data = await res.json();
          if (!res.ok) throw new Error(data?.details || data?.error || 'Tool execution failed. Please try again.');
        } else {
          const formData = new FormData();
          formData.append('userId', user?.email || '');
          formData.append('projectName', 'AW Manager Run');
          const mapper = fieldMap[toolId];
          if (mapper) mapper(filesToUse, formData, lastUsedSpecFilesRef.current);

          const res = await fetch(`${API_BASE}${tool.endpoint}`, {
            method: 'POST',
            body: formData
          });
          data = await res.json();
          if (!res.ok) throw new Error(data?.details || data?.error || 'Tool execution failed. Please try again.');
        }

        // Check if visual analysis was skipped due to file size
        const visualSkipped = data?.processing_info?.visual_skipped === true;

        const skippedNotice = visualSkipped
          ? '⚠️ _Drawing was too large for visual analysis — spec check completed without it. Try a lower-resolution or smaller drawing._\n\n'
          : '';

        // Replace pending with a short summary for the chat bubble
        const reportText = data.analysis || data.result || data.message || data.report || '';
        const lines = reportText.split('\n');

        // Extract critical flags from report
        const criticalLines = lines.filter(l =>
          /critical|NON-COMPLIANT|MISSING|H-00[1-3]/i.test(l) && l.trim().length > 20
        ).slice(0, 3);

        const summaryLines = criticalLines.length > 0
          ? criticalLines.map(l => `⚠️ ${l.trim().replace(/^\|?\s*\d+\s*\|?\s*/, '').slice(0, 120)}`).join('\n')
          : lines.filter(l => l.trim()).slice(0, 4).join('\n');

        const totalFlags = data.flags?.length ||
          (lines.join(' ').match(/\d+\s+(?:total\s+)?(?:items?|flags?|issues?)/i)?.[0]) ||
          null;

        const resultText = [
          totalFlags ? `**Analysis complete** — ${totalFlags} found.` : '**Analysis complete.**',
          '',
          skippedNotice + summaryLines,
          '',
          '_Full report available in the main panel below._'
        ].join('\n');

        setMessages(prev => prev.map(m =>
          m.pending ? {
            ...m,
            text: resultText,
            pending: false,
            isReport: true,
            fullReport: data.analysis || data.result || data.message || data.report || '',
            pdfBase64: data.pdfBase64 || null,
            reportFilename: data.filename || null,
            toolLabel: tool.label
          } : m
        ));

        if (onResult) {
          onResult({
            toolLabel: tool.label,
            report: data.analysis || data.result || data.message || data.report || '',
            pdfBase64: data.pdfBase64 || null,
            filename: data.filename || null,
            timestamp: new Date()
          });
        }

        chainResultsRef.current.push({
          toolLabel: tool.label,
          report: data.analysis || data.result || data.message || data.report || '',
          pdfBase64: data.pdfBase64 || null,
          filename: data.filename || null
        });

        // Add chain suggestions for next tool if more to run
        if (i < toolsToRun.length - 1) {
          setMessages(prev => [...prev, {
            role: 'agent',
            text: `✅ ${tool.label} complete. Running next...`
          }]);
        }
      }

      // All selected tools ran successfully — clear the selection now.
      // (Early returns above for hints/guards intentionally leave it so the
      //  chain buttons stay clickable after the user attaches the right files.)
      setSelected([]);

      if (chainResultsRef.current.length > 1) {
        setMessages(prev => [...prev, {
          role: 'agent',
          text: `**Chain complete** — ${chainResultsRef.current.length} reports generated. Download all as a combined report:`,
          isCombined: true,
          chainResults: [...chainResultsRef.current]
        }]);
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.pending ? { ...m, text: err.message || 'I had trouble running that tool. Please try again.', pending: false } : m
      ));
    } finally {
      setLoading(false);
    }
  };

  const executeToolWithOptions = async (toolId, tool, files, optionValues) => {
    setLoading(true);
    setMessages(prev => [...prev, {
      role: 'agent',
      text: `⚙️ Running ${tool.label}...`,
      pending: true
    }]);

    try {
      if (toolId === 'drawing-compare') {
        const drawingFormData = new FormData();

        if (optionValues?.baselineId) {
          console.log('[DrawingCompare] baselineId:', optionValues.baselineId, 'newDrawing file:', files[0]?.name);
          drawingFormData.append('baselineId', optionValues.baselineId);
          if (files[0]) drawingFormData.append('newDrawing', files[0]);
        } else {
          if (files[0]) drawingFormData.append('baselineDrawing', files[0]);
          if (files[1]) drawingFormData.append('newDrawing', files[1]);
        }

        const userId = user?.email || '';
        const endpoint = `${API_BASE}${tool.endpoint}?userId=${encodeURIComponent(userId)}&projectName=AW+Manager+Run`;

        const toolRes = await fetch(endpoint, { method: 'POST', body: drawingFormData });
        const toolData = await toolRes.json();
        if (!toolRes.ok) throw new Error(toolData?.details || toolData?.error || 'Tool execution failed. Please try again.');

        const a = toolData.analysis || {};
        const oa = a.overallAssessment || {};
        const reportText = [
          `REVISION LEVEL: ${oa.revisionLevel || 'N/A'}`,
          `SUMMARY: ${oa.summary || ''}`,
          `Total Changes: ${oa.totalChangesFound || 0} | High Impact: ${oa.highImpactChanges || 0}`,
        ].join('\n');

        const lines = reportText.split('\n');
        const summaryLines = lines.filter(l => l.trim()).slice(0, 4).join('\n');

        let pdfBase64 = null;
        const filename = `Drawing_Compare_${new Date().toISOString().slice(0, 10)}.pdf`;
        try {
          const pdfRes = await fetch(`${API_BASE}/api/drawing-compare/export-pdf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              analysis: toolData.analysis,
              newDrawingName: toolData.newDrawingName,
              baselineDrawingName: toolData.baselineDrawingName,
              projectName: 'AW Manager Run'
            })
          });
          if (pdfRes.ok) {
            const pdfBuffer = await pdfRes.arrayBuffer();
            const bytes = new Uint8Array(pdfBuffer);
            let binary = '';
            bytes.forEach(b => binary += String.fromCharCode(b));
            pdfBase64 = btoa(binary);
          }
        } catch (e) {
          console.warn('PDF export failed:', e.message);
        }

        setMessages(prev => prev.map(m => m.pending ? {
          ...m,
          text: summaryLines || reportText,
          pending: false,
          report: reportText,
          toolLabel: tool.label,
          timestamp: new Date().toISOString(),
        } : m));

if (onResult) onResult({ toolLabel: tool.label, report: reportText, pdfBase64, filename, timestamp: new Date().toISOString() });
onCacheChange?.();
setPendingToolExecution(null);
setLoading(false);
return;

      }
const formData = new FormData();
formData.append('userId', user?.email || '');
formData.append('projectName', 'AW Manager Run');

// Don't blindly append optionValues for visual tools — fieldMap handles it
if (toolId !== 'design-vs-shop' && toolId !== 'shop-drawing') {
  Object.entries(optionValues).forEach(([key, val]) => {
    formData.append(key, val);
  });
}

const mapper = fieldMap[toolId];
if (mapper) mapper(files, formData, null, optionValues);

      if (toolId === 'spec-update' && optionValues.liveCheck === true) {
        try {
          const profileRes = await fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(user?.email || '')}`);
          const profileData = await profileRes.json();
          const preferred = profileData.profile?.preferredManufacturers || [];
          if (preferred.length > 0) {
            formData.append('temporaryManufacturers', JSON.stringify(
              preferred.map(m => ({ name: m.name, website: '' }))
            ));
          }
        } catch (e) {
          console.warn('Could not load firm profile for spec update');
        }
      }

      const toolRes = await fetch(`${API_BASE}${tool.endpoint}`, {
        method: 'POST',
        body: formData
      });
      const toolData = await toolRes.json();
      if (!toolRes.ok) throw new Error(toolData?.details || toolData?.error || 'Tool execution failed. Please try again.');

      // Handle different tool response shapes
      let reportText = '';
      let pdfBase64 = null;
      let filename = null;

      if (toolId === 'shop-drawing') {
        const review = toolData.review || {};
        const sections = [];

        sections.push(`OVERALL RISK: ${review.overallRisk || 'N/A'}`);
        sections.push(`EXECUTIVE SUMMARY\n${review.executiveSummary || ''}`);

        if (review.criticalIssues?.length) {
          sections.push(`CRITICAL ISSUES\n${review.criticalIssues.map(i => `• ${i}`).join('\n')}`);
        }
        if (review.highPriorityItems?.length) {
          sections.push(`HIGH PRIORITY ITEMS\n${review.highPriorityItems.map(i =>
            typeof i === 'string' ? `• ${i}` : `• ${i.issue || i.description || JSON.stringify(i)}`
          ).join('\n')}`);
        }
        if (review.mediumPriorityItems?.length) {
          sections.push(`MEDIUM PRIORITY ITEMS\n${review.mediumPriorityItems.map(i =>
            typeof i === 'string' ? `• ${i}` : `• ${i.issue || i.description || JSON.stringify(i)}`
          ).join('\n')}`);
        }
        if (review.lowPriorityItems?.length) {
          sections.push(`LOW PRIORITY ITEMS\n${review.lowPriorityItems.map(i =>
            typeof i === 'string' ? `• ${i}` : `• ${i.issue || i.description || JSON.stringify(i)}`
          ).join('\n')}`);
        }
        if (review.missingItems?.length) {
          sections.push(`MISSING ITEMS\n${review.missingItems.map(i => `• ${i}`).join('\n')}`);
        }
        if (review.codeReferences?.length) {
          sections.push(`CODE REFERENCES\n${review.codeReferences.map(i => `• ${i}`).join('\n')}`);
        }
        if (review.recommendations?.length) {
          sections.push(`RECOMMENDATIONS\n${review.recommendations.map(i => `• ${i}`).join('\n')}`);
        }

        reportText = sections.join('\n\n');
        pdfBase64 = toolData.pdf || null;
        filename = `Package_Check_${new Date().toISOString().slice(0, 10)}.pdf`;


      } else if (toolId === 'design-vs-shop') {
        const review = toolData.review || {};
        const sections = [];

        if (review.overallAssessment) sections.push(`OVERALL ASSESSMENT\n${review.overallAssessment}`);
        if (review.summary) sections.push(`SUMMARY\n${review.summary}`);
        if (review.findings?.length) {
          sections.push(`FINDINGS (${review.findings.length})\n${review.findings.map(f =>
            `• [${(f.severity || 'info').toUpperCase()}] ${f.issue || f.description || JSON.stringify(f)}`
          ).join('\n')}`);
        }
        if (review.recommendations?.length) {
          sections.push(`RECOMMENDATIONS\n${review.recommendations.map(i => `• ${i}`).join('\n')}`);
        }

        reportText = sections.join('\n\n') || JSON.stringify(review, null, 2);
        pdfBase64 = toolData.pdf || null;
        filename = `Design_vs_Shop_${new Date().toISOString().slice(0, 10)}.pdf`;
      } else {
        reportText = toolData.analysis || toolData.result || toolData.message || toolData.report || '';
        pdfBase64 = toolData.pdfBase64 || null;
        filename = toolData.filename || null;
      }

      const lines = reportText.split('\n');
      const summaryLines = lines.filter(l => l.trim()).slice(0, 4).join('\n');
      const totalFlags = toolData.flags?.length ||
        (lines.join(' ').match(/\d+\s+(?:total\s+)?(?:items?|flags?|issues?)/i)?.[0]) || null;

      const resultText = [
        totalFlags ? `**Analysis complete** — ${totalFlags} found.` : '**Analysis complete.**',
        '', summaryLines, '',
        '_Full report available in the main panel below._'
      ].join('\n');

      lastReportRef.current = { fullReport: reportText, toolLabel: tool.label, timestamp: new Date() };
      chainResultsRef.current.push({ toolLabel: tool.label, report: reportText, pdfBase64, filename });

      setMessages(prev => prev.map(m => m.pending ? {
        ...m, text: resultText, pending: false, isReport: true,
        fullReport: reportText, pdfBase64,
        reportFilename: filename, toolLabel: tool.label
      } : m));

if (onResult) onResult({ toolLabel: tool.label, report: reportText, pdfBase64, filename, timestamp: new Date() });
onCacheChange?.();


console.log('[executeToolWithOptions] pendingToolExecution:', JSON.stringify(pendingToolExecution));
const chains = pendingToolExecution?.chainSuggestions || [];
console.log('[executeToolWithOptions] chains:', chains);

if (chains.length > 0) {
  setMessages(prev => [...prev, {
    role: 'agent',
    text: 'Would you like to continue with any of these?',
    chainOptions: chains.map(t => ({ icon: '🔧', label: t.label, desc: `Run ${t.label} on your documents` }))
  }]);
}

    } catch (err) {
      setMessages(prev => prev.map(m => m.pending ? {
        ...m, text: err.message || 'Tool execution failed. Please try again.', pending: false
      } : m));
    } finally {
      setLoading(false);
      setAttachedFiles([]);
      attachedFilesRef.current = [];
    }
  };

  const selectToolOption = async (optionValue) => {
    if (!pendingToolExecution) return;
    const { toolId, tool, files } = pendingToolExecution;

    if (toolId === 'drawing-compare' && optionValue.baselineType === 'vault') {
      console.log('[SelectOption] vault baselineId:', optionValue.baselineId, 'storing in pendingToolExecution');
      setPendingToolExecution({ toolId, tool, files: [...attachedFilesRef.current], optionValues: optionValue });
      setMessages(prev => [...prev, {
        role: 'agent',
        text: '✅ Baseline selected from vault. Any specific elements to focus on? Type them below or leave blank, then attach the new drawing and click Run.',
        isDrawingCompareReady: true,
        isCustomFocusPrompt: true
      }]);
      setLoading(false);
      return;
    }

    if (toolId === 'drawing-compare' && optionValue.baselineType === 'upload') {
      setPendingToolExecution({ toolId, tool, files: [...attachedFilesRef.current], optionValues: optionValue });
      setMessages(prev => [...prev, {
        role: 'agent',
        text: 'Please attach the **baseline (previous) drawing** first, then the **new drawing**. Any specific elements to focus on? Type them in the message box (optional), then click Run.',
        isDrawingCompareReady: true,
        isCustomFocusPrompt: true
      }]);
      setLoading(false);
      return;
    }

if (toolId === 'design-vs-shop') {
  setPendingToolExecution({ toolId, tool, files, optionValues: optionValue });
  setMessages(prev => [...prev, {
    role: 'agent',
    text: `Got it — **${optionValue.trade || 'General'}** trade selected. Now attach the **shop drawings** and click Run.`,
    isDrawingCompareReady: true
  }]);
  setTimeout(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, 150);
  setLoading(false);
  return;
}

    setPendingToolExecution(null);

    const toolOpts = TOOL_OPTIONS[toolId];
    if (toolOpts?.followUp) {
      setPendingToolExecution({ toolId, tool, files, optionValues: optionValue });
      setSelectedPriorities([]);
      setMessages(prev => [...prev, {
        role: 'agent',
        text: toolOpts.followUp.question,
        pending: false,
        isToolFollowUp: true,
        followUpOptions: toolOpts.followUp.options,
        toolId
      }]);
      setLoading(false);
      return;
    }

    await executeToolWithOptions(toolId, tool, files, optionValue);
  };

  const runWithPriorities = async () => {
    if (!pendingToolExecution) return;
    const { toolId, tool, files, optionValues } = pendingToolExecution;
    setPendingToolExecution(null);

    const priorityString = selectedPriorities.join(',');
    const finalOptions = {
      ...optionValues,
      ...(priorityString ? { priorityAreas: priorityString } : {})
    };
    setSelectedPriorities([]);

    await executeToolWithOptions(toolId, tool, files, finalOptions);
  };

  const sendMessage = async () => {
    const inputText = input.trim();
    if (!inputText || loading) return;

    // Spec Builder conversational flow
if (pendingSpecBuilder) {
  if (pendingSpecBuilder.step === 'requirements') {
    setPendingSpecBuilder({ ...pendingSpecBuilder, requirements: inputText, step: 'sections' });
    setMessages(prev => [
      ...prev,
      { role: 'user', text: inputText },
      { role: 'agent', text: 'Got it. Now, which **sections** should I include?\n\n_e.g. "Part 1 General, Part 2 Products, Part 3 Execution, submittals, quality assurance"_' }
    ]);
    setInput('');
    setLoading(false);
    return;
  }

  if (pendingSpecBuilder.step === 'sections') {
    const { files, requirements } = pendingSpecBuilder;
    const sections = inputText;
    setPendingSpecBuilder(null);
    setMessages(prev => [...prev, { role: 'user', text: inputText }]);
    setInput('');

    const tool = TOOL_REGISTRY['spec-builder'];
    setLoading(true);
    setMessages(prev => [...prev, { role: 'agent', text: '⚙️ Building your specification...', pending: true }]);

    const formData = new FormData();
    formData.append('userId', user?.email || '');
    formData.append('projectName', 'AW Manager Run');
    formData.append('requirements', requirements);
    formData.append('sections', sections);
    if (files[0]) formData.append('masterSpec', files[0]);
    if (files[1]) formData.append('template', files[1]);
    else if (files[0]) formData.append('template', files[0]); // same file fallback

    try {
      const toolRes = await fetch(`${API_BASE}${tool.endpoint}`, { method: 'POST', body: formData });
      const toolData = await toolRes.json();
      if (!toolRes.ok) throw new Error(toolData?.details || toolData?.error || 'Spec Builder failed. Please try again.');
      const reportText = toolData.report || toolData.result || toolData.message || '';

      setMessages(prev => prev.map(m => m.pending ? {
        ...m,
        text: '**Specification built.** Full spec available in the main panel.',
        pending: false,
        isReport: true,
        fullReport: reportText,
        pdfBase64: toolData.pdfBase64 || null,
        reportFilename: toolData.filename || null,
        toolLabel: tool.label
      } : m));

      if (onResult) onResult({
        toolLabel: tool.label,
        report: reportText,
        pdfBase64: toolData.pdfBase64 || null,
        filename: toolData.filename || null,
        timestamp: new Date()
      });
    } catch (err) {
      setMessages(prev => prev.map(m => m.pending ? { ...m, text: err.message || 'Spec Builder failed. Please try again.', pending: false } : m));
    } finally {
      setLoading(false);
      setAttachedFiles([]);
      attachedFilesRef.current = [];
    }
    return;
  }
}

    // Snapshot the prior thread for the backend (exclude chain-only entries).
const conversationHistory = messages
  .filter((m) => m.role !== 'chain')
  .slice(-10)  // keep last 10 messages only
  .map((m) => ({
    role: m.role === 'user' ? 'user' : 'assistant',
    content: typeof m.text === 'string' ? m.text.slice(0, 500) : '',
  }));

    // Show the user message and a pending "thinking" bubble immediately.
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: inputText },
      { role: 'agent', text: 'AW Manager is thinking...', pending: true },
    ]);
    setInput('');
    setLoading(true);

    // Convert attached files to base64 for sending (chunk-safe via FileReader).
    const toBase64 = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

const MAX_DISPATCH_SIZE = 150 * 1024 * 1024; // 150MB
const filePayloads = await Promise.all(attachedFiles.map(async (f) => {
  if (f.size > MAX_DISPATCH_SIZE) {
    return { name: f.name, mimeType: f.type, data: null, skipped: true };
  }
  return { name: f.name, mimeType: f.type, data: await toBase64(f) };
}));

    // Replace the pending placeholder with a real (or fallback) agent message.
    const replacePending = (agentMsg) =>
      setMessages((prev) => {
        const next = [...prev];
        const idx = next.findIndex((m) => m.pending);
        if (idx !== -1) next[idx] = agentMsg;
        else next.push(agentMsg);
        return next;
      });

    try {
      chainResultsRef.current = []; // Reset chain for new conversation turn
      const res = await fetch(`${API_BASE}/api/manager/dispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: inputText,
          activeTab,
          cachedDocs,
          userId: user?.email || '',
          conversationHistory,
          files: filePayloads,
          lastResultSummary: lastResultSummary ? {
            toolLabel: lastResultSummary.toolLabel,
            ranAt: lastResultSummary.timestamp,
            summary: lastResultSummary.snippet,
            fullReport: (lastResultSummary.fullReport || '').slice(0, 1500)
          } : null,
        }),
      });
      const data = await res.json();

      const agentMsg = { role: 'agent', text: data.response };
      if (data.chainSuggestions?.length > 0 && !(data.tools?.length > 0 && attachedFiles.length > 0)) {
        agentMsg.chainOptions = data.chainSuggestions.map((t) => ({
          icon: '🔧',
          label: t.label,
          desc: `Run ${t.label} on your documents`,
        }));
      }

      replacePending(agentMsg);
      
const filesBeforeDispatch = [...attachedFiles];
lastUsedFilesRef.current = filesBeforeDispatch;
lastUsedSpecFilesRef.current = filesBeforeDispatch.filter(f => !f.type?.startsWith('image/') && !DRAWING_NAME_RE.test(f.name));
const fileCountBeforeDispatch = filesBeforeDispatch.length;
setAttachedFiles([]);
attachedFilesRef.current = [];

      // Auto-attach suggested cached docs by name reference
      let autoAttachedDocs = false;
      if (data.suggestedDocIds?.length > 0) {
        const docsToAttach = docs.filter(d =>
          data.suggestedDocIds.includes(d.id) &&
          !attachedFilesRef.current.some(f => f.name === d.name)
        );

        if (docsToAttach.length > 0) {
          const autoFiles = [];
          for (const doc of docsToAttach) {
            try {
              const res = await fetch(
                `${API_BASE}/api/documents/${doc.id}/download?userId=${encodeURIComponent(user?.email || '')}&t=${Date.now()}`,
                { cache: 'no-store' }
              );
              if (!res.ok) continue;
              const blob = await res.blob();
              const file = new File([blob], doc.name, { type: doc.mimeType || 'application/pdf' });
              autoFiles.push(file);

            } catch (err) {

            }
          }

          if (autoFiles.length > 0) {
            attachedFilesRef.current = autoFiles;
            setAttachedFiles(autoFiles);
            autoAttachedDocs = true;

            setMessages(prev => [...prev, {
              role: 'agent',
              text: `📎 Auto-attaching: ${autoFiles.map(f => f.name).join(', ')}`
            }]);
          }
        }
      }

      // Trigger tool execution after auto-attaching docs
      if (autoAttachedDocs && data.tools?.length > 0) {
        const toolId = data.tools[0];
        const tool = TOOL_REGISTRY[toolId];

        // Fast path: 2 files attached directly — run drawing-compare immediately
if (toolId === 'drawing-compare' && fileCountBeforeDispatch >= 2) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    if (filesBeforeDispatch[0].name === filesBeforeDispatch[1].name) {
      setMessages(prev => prev.map(m => m.pending ? {
        ...m,
        text: '⚠️ Both drawings have the same filename. Please attach two different drawing files to compare.',
        pending: false
      } : m));
      setLoading(false);
      return;
    }
    await executeToolWithOptions(toolId, tool, filesBeforeDispatch, { baselineType: 'upload' });
    return;
  }
}

        // Drawing compare — only intercept for vault selection when fewer than 2 files attached
if (toolId === 'drawing-compare' && fileCountBeforeDispatch < 2) {
  const vaultOpts = (vaultDrawings || []).map(d => ({
    label: `🗄 ${d.fileName}${d.sheetNumber ? ` (Sheet ${d.sheetNumber})` : ''}`,
    desc: `Rev ${d.revisionNumber || 'N/A'} · ${d.daysLeft}d left`,
    value: { baselineId: d.id, baselineType: 'vault' }
  }));
  const uploadOpt = { label: '📁 Upload Baseline Drawing', desc: 'Attach baseline then new drawing manually', value: { baselineType: 'upload' } };
  if (vaultOpts.length === 0 && fileCountBeforeDispatch === 1) {
    setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch, optionValues: { baselineType: 'upload' } });
    setMessages(prev => [...prev, {
      role: 'agent',
      text: '✅ Baseline attached. Now attach the **new drawing** to compare, then click Run Comparison.',
      isDrawingCompareReady: true
    }]);
    setLoading(false);
    return;
  }
          const allOpts = vaultOpts.length > 0 ? [...vaultOpts, uploadOpt] : [uploadOpt];
      setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch });
          setMessages(prev => [...prev, {
            role: 'agent',
            text: vaultOpts.length > 0 ? 'Select a baseline drawing from your vault:' : 'Attach baseline then new drawing.',
            isToolOptions: true,
            toolOptions: allOpts,
            toolId
          }]);
          setLoading(false);
          return;
        }


        // Shop drawing needs trade discipline selection
if (toolId === 'shop-drawing' && TOOL_OPTIONS[toolId]) {
  const toolOpts = TOOL_OPTIONS[toolId];
  setPendingToolExecution({ toolId, tool, files: [...attachedFilesRef.current] });
  setMessages(prev => [...prev, {
    role: 'agent',
    text: toolOpts.question,
    isToolOptions: true,
    toolOptions: toolOpts.options,
    toolId
  }]);
  setLoading(false);
  return;
}


// Design vs Shop — two-message flow
if (toolId === 'design-vs-shop') {
  const files = [...attachedFilesRef.current];
  setPendingToolExecution({ toolId, tool, files });
  attachedFilesRef.current = [];
  setAttachedFiles([]);
  setMessages(prev => [...prev, {
    role: 'agent',
    text: `Got your ${files.length} design/contract drawing${files.length > 1 ? 's' : ''}. Now attach the **shop drawings** and select the trade discipline.`,
    isToolOptions: true,
    toolOptions: TOOL_OPTIONS['design-vs-shop'].options.map(opt => ({
      ...opt,
      value: { ...opt.value, designCount: files.length }
    })),
    toolId
  }]);
  setLoading(false);
  return;
}


// Track Changes — two-message flow
if (toolId === 'track-changes' && filesBeforeDispatch.length > 0) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    const specFiles = filesBeforeDispatch.filter(f => !f.type?.startsWith('image/'));
    setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch });
    setMessages(prev => prev.map(m => m.pending ? {
      ...m,
      text: `Got your master spec (${specFiles.length} file${specFiles.length > 1 ? 's' : ''}). Now attach the **customer/outbound spec** and I'll compare them.`,
      pending: false,
      isDrawingCompareReady: true,
      toolId
    } : m));
    setLoading(false);
    return;
  }
}

// Spec Builder — start conversational flow
if (toolId === 'spec-builder' && attachedFilesRef.current.length > 0) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    const files = [...attachedFilesRef.current];
    setPendingSpecBuilder({ files, step: 'requirements', requirements: '' });
    attachedFilesRef.current = [];
    setAttachedFiles([]);
    setMessages(prev => [...prev, {
      role: 'agent',
      text: `Got your ${files.length === 1 ? 'master spec (will also use as template)' : 'master spec and template'}.\n\nWhat are the **component requirements** for this spec?\n\n_e.g. "Custom central-station AHU units for a hospital mechanical room, 10,000–50,000 CFM range"_`
    }]);
    setLoading(false);
    return;
  }
}

// Spec Check with auto-attached docs — prompt user to attach the submittal
if (toolId === 'spec-check' && attachedFilesRef.current.length >= 1) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    const uniqueFiles = attachedFilesRef.current.filter((f, idx, arr) => arr.findIndex(x => x.name === f.name) === idx);
    const images = uniqueFiles.filter(f => f.type?.startsWith('image/'));
    const allPdfs = uniqueFiles.filter(f => !f.type?.startsWith('image/'));
    const pdfDrawings = allPdfs.filter(f => DRAWING_NAME_RE.test(f.name));
    const specPdfs = allPdfs.filter(f => !DRAWING_NAME_RE.test(f.name));
    const autoDrawings = [...images, ...pdfDrawings];

    const contextParts = [];
    if (specPdfs.length > 0) contextParts.push(`${specPdfs.length} spec file${specPdfs.length > 1 ? 's' : ''}`);
    if (autoDrawings.length > 0) contextParts.push(`${autoDrawings.length} drawing${autoDrawings.length > 1 ? 's' : ''}`);

    setPendingToolExecution({ toolId, tool, files: uniqueFiles, chainSuggestions: data.chainSuggestions || [] });
    attachedFilesRef.current = [];
    setAttachedFiles([]);
    setMessages(prev => [...prev, {
      role: 'agent',
      text: `Got your ${contextParts.join(' and ')}. Now attach the **submittal** and I'll run the analysis.`,
      isDrawingCompareReady: true,
      toolId
    }]);
    setLoading(false);
    return;
  }
}


        if (tool && attachedFilesRef.current.length > 0) {
          setMessages(prev => [...prev, {
            role: 'agent',
            text: `⚙️ Running ${tool.label}...`,
            pending: true
          }]);

          try {

            const formData = new FormData();
            formData.append('userId', user?.email || '');
            formData.append('projectName', 'AW Manager Run');
            const mapper = fieldMap[toolId];
            if (mapper) mapper(attachedFilesRef.current, formData, null, pendingToolExecution?.optionValues);

            const toolRes = await fetch(`${API_BASE}${tool.endpoint}`, {
              method: 'POST',
              body: formData
            });
            const toolData = await toolRes.json();
            if (!toolRes.ok) throw new Error(toolData?.details || toolData?.error || 'Tool execution failed. Please try again.');

            const reportText = toolData.analysis || toolData.result || toolData.message || toolData.report || '';
            const lines = reportText.split('\n');
            const criticalLines = lines.filter(l =>
              /critical|NON-COMPLIANT|MISSING|H-00[1-3]/i.test(l) && l.trim().length > 20
            ).slice(0, 3);
            const summaryLines = criticalLines.length > 0
              ? criticalLines.map(l => `⚠️ ${l.trim().replace(/^\|?\s*\d+\s*\|?\s*/, '').slice(0, 120)}`).join('\n')
              : lines.filter(l => l.trim()).slice(0, 4).join('\n');
            const totalFlags = toolData.flags?.length ||
              (lines.join(' ').match(/\d+\s+(?:total\s+)?(?:items?|flags?|issues?)/i)?.[0]) || null;
            const skippedNotice = toolData?.processing_info?.visual_skipped === true
              ? '⚠️ _Drawing was too large for visual analysis — spec check completed without it._\n\n' : '';
            const resultText = [
              totalFlags ? `**Analysis complete** — ${totalFlags} found.` : '**Analysis complete.**',
              '', skippedNotice + summaryLines, '',
              '_Full report available in the main panel below._'
            ].join('\n');

            lastReportRef.current = { fullReport: reportText, toolLabel: tool.label, timestamp: new Date() };
            chainResultsRef.current.push({ toolLabel: tool.label, report: reportText, pdfBase64: toolData.pdfBase64 || null, filename: toolData.filename || null });

            setMessages(prev => prev.map(m => m.pending ? {
              ...m, text: resultText, pending: false, isReport: true,
              fullReport: reportText, pdfBase64: toolData.pdfBase64 || null,
              reportFilename: toolData.filename || null, toolLabel: tool.label
            } : m));

            if (onResult) onResult({ toolLabel: tool.label, report: reportText, pdfBase64: toolData.pdfBase64 || null, filename: toolData.filename || null, timestamp: new Date() });

            if (data.chainSuggestions?.length > 0) {
              setMessages(prev => [...prev, {
                role: 'agent',
                text: 'Would you like to continue with any of these?',
                chainOptions: data.chainSuggestions.map(t => ({ icon: '🔧', label: t.label, desc: `Run ${t.label} on your documents` }))
              }]);
            }

            if (toolId === 'spec-check' && criticalLines.length > 0) {
              setMessages(prev => [...prev, {
                role: 'agent',
                text: `I found **${criticalLines.length} critical issue${criticalLines.length > 1 ? 's' : ''}** that may need RFIs. Want me to draft RFI language for these now?`,
                chainOptions: [{ icon: '📝', label: 'Draft RFIs', desc: `Auto-draft RFIs for ${criticalLines.length} critical issue${criticalLines.length > 1 ? 's' : ''}` }]
              }]);
            }

            if (data.knowledgeAnswer) {
              setMessages(prev => [...prev, { role: 'agent', text: '💡 **AEC Insight:**\n\n' + data.knowledgeAnswer, isKnowledge: true }]);
            }
          } catch (err) {
            setMessages(prev => prev.map(m => m.pending ? { ...m, text: err.message || 'Tool execution failed. Please try again.', pending: false } : m));
          } finally {
            setAttachedFiles([]);
            attachedFilesRef.current = [];
          }
          return;
        }
      }


// If no tool auto-execution, show knowledge answer now (tool runs show it after chain suggestions)
      if (data.knowledgeAnswer) {
        setMessages(prev => [...prev, {
          role: 'agent',
          text: data.knowledgeAnswer,
          isKnowledge: true
        }]);
      }

      // Auto-execute the dispatched tool if files are attached and a tool was identified
      const toolId = data.tools?.[0];

// Fast path: 2 files attached directly — run drawing-compare immediately
if (toolId === 'drawing-compare' && fileCountBeforeDispatch >= 2) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    if (filesBeforeDispatch[0].name === filesBeforeDispatch[1].name) {
      setMessages(prev => prev.map(m => m.pending ? {
        ...m,
        text: '⚠️ Both drawings have the same filename. Please attach two different drawing files to compare.',
        pending: false
      } : m));
      setLoading(false);
      return;
    }
    await executeToolWithOptions(toolId, tool, filesBeforeDispatch, { baselineType: 'upload' });
    return;
  }
}
      // Drawing compare — only intercept for vault selection when fewer than 2 files attached
if (toolId === 'drawing-compare' && fileCountBeforeDispatch < 2) {
        const tool = TOOL_REGISTRY[toolId];
        if (tool) {
          const vaultOpts = (vaultDrawings || []).map(d => ({
            label: `🗄 ${d.fileName}${d.sheetNumber ? ` (Sheet ${d.sheetNumber})` : ''}`,
            desc: `Rev ${d.revisionNumber || 'N/A'} · ${d.daysLeft}d left`,
            value: { baselineId: d.id, baselineType: 'vault' }
          }));
          const uploadOpt = { label: '📁 Upload Baseline Drawing', desc: 'Attach baseline then new drawing manually', value: { baselineType: 'upload' } };
          if (vaultOpts.length === 0 && fileCountBeforeDispatch === 1) {
            setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch, optionValues: { baselineType: 'upload' } });
            setMessages(prev => prev.map(m => m.pending ? {
              ...m,
              text: '✅ Baseline attached. Now attach the **new drawing** to compare, then click Run Comparison.',
              pending: false,
              isDrawingCompareReady: true
            } : m));
            setLoading(false);
            return;
          }
          const allOpts = vaultOpts.length > 0 ? [...vaultOpts, uploadOpt] : [uploadOpt];
          setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch });
          setMessages(prev => prev.map(m => m.pending ? {
            ...m,
            text: vaultOpts.length > 0 ? 'Select a baseline drawing from your vault:' : 'Attach baseline drawing then new drawing.',
            pending: false,
            isToolOptions: true,
            toolOptions: allOpts,
            toolId
          } : m));
          setLoading(false);
          return;
        }
      }

      // Design vs Shop — intercept with filesBeforeDispatch since attachedFilesRef is cleared
// Design vs Shop — two-message flow (design drawings first, then shop drawings)
if (toolId === 'design-vs-shop' && filesBeforeDispatch.length > 0) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch });
    setMessages(prev => prev.map(m => m.pending ? {
      ...m,
      text: `Got your ${filesBeforeDispatch.length} design/contract drawing${filesBeforeDispatch.length > 1 ? 's' : ''}. Now attach the **shop drawings** and click Run.`,
      pending: false,
      isDrawingCompareReady: true,
      toolId
    } : m));
    setLoading(false);
    return;
  }
}
// Spec Builder — start conversational flow
if (toolId === 'design-vs-shop' && filesBeforeDispatch.length > 0) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch });
    setMessages(prev => prev.map(m => m.pending ? {
      ...m,
      text: `Got your ${filesBeforeDispatch.length} design/contract drawing${filesBeforeDispatch.length > 1 ? 's' : ''}. Select the trade discipline, then attach the **shop drawings** and click Run.`,
      pending: false,
      isToolOptions: true,
      toolOptions: TOOL_OPTIONS['design-vs-shop'].options.map(opt => ({
        ...opt,
        value: { ...opt.value, designCount: filesBeforeDispatch.length }
      })),
      toolId
    } : m));
    setLoading(false);
    return;
  }
}

// Track Changes — two-message flow
if (toolId === 'track-changes' && filesBeforeDispatch.length > 0) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    const specFiles = filesBeforeDispatch.filter(f => !f.type?.startsWith('image/'));
    setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch });
    setMessages(prev => prev.map(m => m.pending ? {
      ...m,
      text: `Got your master spec (${specFiles.length} file${specFiles.length > 1 ? 's' : ''}). Now attach the **customer/outbound spec** and I'll compare them.`,
      pending: false,
      isDrawingCompareReady: true,
      toolId
    } : m));
    setLoading(false);
    return;
  }
}
// Spec Check with multiple files — ask user to identify the submittal
if (toolId === 'spec-check' && filesBeforeDispatch.length > 0) {
  const tool = TOOL_REGISTRY[toolId];
  if (tool) {
    const images = filesBeforeDispatch.filter(f => f.type?.startsWith('image/'));
    const uniqueFiles = filesBeforeDispatch.filter((f, idx, arr) => arr.findIndex(x => x.name === f.name) === idx);
    const allPdfs = filesBeforeDispatch.filter(f => !f.type?.startsWith('image/'));
    const pdfDrawings = allPdfs.filter(f => DRAWING_NAME_RE.test(f.name));
    const specPdfs = allPdfs.filter(f => !DRAWING_NAME_RE.test(f.name));
    const autoDrawings = [...images, ...pdfDrawings];

    const contextParts = [];
    if (specPdfs.length > 0) contextParts.push(`${specPdfs.length} spec file${specPdfs.length > 1 ? 's' : ''}`);
    if (autoDrawings.length > 0) contextParts.push(`${autoDrawings.length} drawing${autoDrawings.length > 1 ? 's' : ''}`);
  
  console.log('[sendMessage] spec-check chainSuggestions being stashed:', data.chainSuggestions);
  setPendingToolExecution({ toolId, tool, files: filesBeforeDispatch, chainSuggestions: data.chainSuggestions || [] });
  setMessages(prev => prev.map(m => m.pending ? {
    ...m,
    text: `Got your ${contextParts.join(' and ')}. Now attach the **submittal** and I'll run the analysis.`,
    pending: false,
    isDrawingCompareReady: true,
    toolId
  } : m));
    setLoading(false);
    return;
  }
}
      if (data.tools?.length > 0 && attachedFilesRef.current.length > 0) {
        if (pendingToolExecution) return;
        const tool = TOOL_REGISTRY[toolId];
        if (tool) {
          // Add running indicator
          setMessages(prev => [...prev, {
            role: 'agent',
            text: `⚙️ Running ${tool.label}...`,
            pending: true
          }]);

          // Spec Check needs 2+ non-drawing PDFs (spec + submittal) — drawings route separately
          const pdfFiles = attachedFilesRef.current.filter(f => !f.type?.startsWith('image/'));
          const specPdfs = pdfFiles.filter(f => !DRAWING_NAME_RE.test(f.name));
          if (toolId === 'spec-check' && specPdfs.length < 2 && !autoAttachedDocs) {
            setMessages(prev => prev.map(m =>
              m.pending ? {
                ...m,
                text: 'I need both a specification PDF and a submittal PDF to run Spec Check. Images and drawing PDFs (arch, floor plan, etc.) attach as drawings automatically. Please attach at least 2 non-drawing PDF files and try again.',
                pending: false
              } : m
            ));
            return;
          }

          if (toolId === 'drawing-compare' && !pendingToolExecution?.optionValues) {
            const vaultOpts = (vaultDrawings || []).map(d => ({
              label: `🗄 ${d.fileName}${d.sheetNumber ? ` (Sheet ${d.sheetNumber})` : ''}`,
              desc: `Rev ${d.revisionNumber || 'N/A'} · ${d.daysLeft}d left in vault`,
              value: { baselineId: d.id, baselineType: 'vault' }
            }));
            const uploadOpt = {
              label: '📁 Upload Baseline Drawing',
              desc: 'Attach a baseline drawing file manually',
              value: { baselineType: 'upload' }
            };
            const allOpts = [...vaultOpts, uploadOpt];
            setPendingToolExecution({ toolId, tool, files: [...attachedFilesRef.current] });
            setMessages(prev => prev.map(m => m.pending ? {
              ...m,
              text: vaultOpts.length > 0
                ? 'Select a baseline drawing from your vault, or upload one:'
                : 'No vault drawings found. Please attach both drawings — the baseline (previous) first, then the new drawing.',
              pending: false,
              isToolOptions: true,
              toolOptions: allOpts,
              toolId
            } : m));
            setLoading(false);
            return;
          }

          if (TOOL_OPTIONS[toolId] && !pendingToolExecution?.optionValues) {
            const toolOpts = TOOL_OPTIONS[toolId];
            setPendingToolExecution({ toolId, tool, files: [...attachedFilesRef.current] });
            setMessages(prev => prev.map(m => m.pending ? {
              ...m,
              text: toolOpts.question,
              pending: false,
              isToolOptions: true,
              toolOptions: toolOpts.options,
              toolId
            } : m));
            setLoading(false);
            return;
          }

          try {
            const formData = new FormData();
            formData.append('userId', user?.email || '');
            formData.append('projectName', 'AW Manager Run');
            const mapper = fieldMap[toolId];
            if (mapper) mapper(attachedFilesRef.current, formData, null, pendingToolExecution?.optionValues);

            const toolRes = await fetch(`${API_BASE}${tool.endpoint}`, {
              method: 'POST',
              body: formData
            });
            const toolData = await toolRes.json();
            if (!toolRes.ok) throw new Error(toolData?.details || toolData?.error || 'Tool execution failed. Please try again.');

            // Check if visual analysis was skipped due to file size
            const visualSkipped = toolData?.processing_info?.visual_skipped === true;

            // Extract a short summary for the chat bubble
            const reportText = toolData.analysis || toolData.result || toolData.message || toolData.report || '';
            const lines = reportText.split('\n');

            // Extract critical flags from report
            const criticalLines = lines.filter(l =>
              /critical|NON-COMPLIANT|MISSING|H-00[1-3]/i.test(l) && l.trim().length > 20
            ).slice(0, 3);

            const summaryLines = criticalLines.length > 0
              ? criticalLines.map(l => `⚠️ ${l.trim().replace(/^\|?\s*\d+\s*\|?\s*/, '').slice(0, 120)}`).join('\n')
              : lines.filter(l => l.trim()).slice(0, 4).join('\n');

            const totalFlags = toolData.flags?.length ||
              (lines.join(' ').match(/\d+\s+(?:total\s+)?(?:items?|flags?|issues?)/i)?.[0]) ||
              null;

            const skippedNotice = visualSkipped
              ? '⚠️ _Drawing was too large for visual analysis — spec check completed without it. Try a lower-resolution or smaller drawing._\n\n'
              : '';

            const resultText = [
              totalFlags ? `**Analysis complete** — ${totalFlags} found.` : '**Analysis complete.**',
              '',
              skippedNotice + summaryLines,
              '',
              '_Full report available in the main panel below._'
            ].join('\n');

            setMessages(prev => prev.map(m =>
              m.pending ? {
                ...m,
                text: resultText,
                pending: false,
                isReport: true,
                fullReport: toolData.analysis || toolData.result || toolData.message || toolData.report || '',
                pdfBase64: toolData.pdfBase64 || null,
                reportFilename: toolData.filename || null,
                toolLabel: tool.label
              } : m
            ));

            lastReportRef.current = {
              fullReport: toolData.analysis || toolData.result || toolData.message || toolData.report || '',
              toolLabel: tool.label,
              timestamp: new Date()
            };

            // Add to chain results for combined report
            chainResultsRef.current.push({
              toolLabel: tool.label,
              report: toolData.analysis || toolData.result || toolData.message || toolData.report || '',
              pdfBase64: toolData.pdfBase64 || null,
              filename: toolData.filename || null
            });

            if (onResult) {
              onResult({
                toolLabel: tool.label,
                report: toolData.analysis || toolData.result || toolData.message || toolData.report || '',
                pdfBase64: toolData.pdfBase64 || null,
                filename: toolData.filename || null,
                timestamp: new Date()
              });
            }

            // Show chain suggestions after tool completes
            if (data.chainSuggestions?.length > 0) {
              setMessages(prev => [...prev, {
                role: 'agent',
                text: 'Would you like to continue with any of these?',
                chainOptions: data.chainSuggestions.map(t => ({
                  icon: '🔧',
                  label: t.label,
                  desc: `Run ${t.label} on your documents`
                }))
              }]);
            }
            

            // Proactive RFI drafting suggestion when critical flags found
            if (toolId === 'spec-check' && criticalLines.length > 0) {
              setMessages(prev => [...prev, {
                role: 'agent',
                text: `I found **${criticalLines.length} critical issue${criticalLines.length > 1 ? 's' : ''}** that may need RFIs. Want me to draft RFI language for these now?`,
                chainOptions: [{
                  icon: '📝',
                  label: 'Draft RFIs',
                  desc: `Auto-draft RFIs for ${criticalLines.length} critical issue${criticalLines.length > 1 ? 's' : ''}`
                }]
              }]);
            }

            // Show knowledge answer after tool execution and chain suggestions
          if (data.knowledgeAnswer) {
            setMessages(prev => [...prev, {
              role: 'agent',
              text: data.knowledgeAnswer,
              isKnowledge: true
            }]);
          }

          } catch (err) {
            setMessages(prev => prev.map(m =>
              m.pending ? { ...m, text: err.message || 'Tool execution failed. Please try again.', pending: false } : m
            ));
          } finally {
            setAttachedFiles([]);
            attachedFilesRef.current = [];
          }
        }
      }
    } catch (err) {
      replacePending({
        role: 'agent',
        text: 'I had trouble completing that analysis. Try attaching your files again and re-sending, or refresh the browser if the issue persists.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '52px',
        right: 0,
        width: `${sidebarWidth}px`,
        height: 'calc(100vh - 52px)',
        background: PANEL_BG,
        borderLeft: `1px solid ${BORDER}`,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'sans-serif',
        boxSizing: 'border-box',
        zIndex: 900,
      }}
    >
      {resizable && (
        <div
          onMouseDown={() => setIsDragging(true)}
          style={{
            position: 'absolute',
            left: '-3px',
            top: 0,
            width: '6px',
            height: '100%',
            cursor: 'col-resize',
            zIndex: 901,
            background: isDragging ? GOLD : 'transparent',
          }}
        />
      )}
      <style>{`
        @keyframes ztManagerPulse {
          0%   { transform: scale(1);   opacity: 1; }
          50%  { transform: scale(1.6); opacity: 0.4; }
          100% { transform: scale(1);   opacity: 1; }
        }
        .zt-manager-thread::-webkit-scrollbar { width: 4px; }
        .zt-manager-thread::-webkit-scrollbar-track { background: transparent; }
        .zt-manager-thread::-webkit-scrollbar-thumb { background: #252a38; border-radius: 4px; }
        .zt-manager-thread::-webkit-scrollbar-thumb:hover { background: #D4A417; }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '14px 16px',
          borderBottom: `1px solid ${BORDER}`,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: '9px',
            height: '9px',
            borderRadius: '50%',
            background: GOLD,
            flexShrink: 0,
            animation: 'ztManagerPulse 1.6s ease-in-out infinite',
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#f3f4f6' }}>AW Manager</div>
          <div style={{ fontSize: '11px', color: '#7a8099' }}>AI orchestration agent</div>
        </div>
        <button
          type="button"
          onClick={clearMessages}
          style={{
            fontSize: '11px',
            padding: '4px 10px',
            borderRadius: '4px',
            border: `1px solid ${BORDER}`,
            background: 'transparent',
            color: '#7a8099',
            cursor: 'pointer',
            fontFamily: 'sans-serif',
          }}
        >
          Clear
        </button>
      </div>

      {/* Message thread */}
      <div
        ref={threadRef}
        className="zt-manager-thread"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#252a38 transparent',
        }}
      >
        {messages.map((msg, i) => {
          const isUser = msg.role === 'user';
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isUser ? 'flex-end' : 'flex-start',
              }}
            >
              <span style={{ fontSize: '10px', color: '#5b627a', marginBottom: '4px' }}>
                {isUser ? 'You' : 'AW Manager'}
              </span>
              <div style={{
                maxWidth: msg.isKnowledge ? '95%' : '85%',
                padding: '9px 12px',
                borderRadius: '10px',
                fontSize: '13px',
                lineHeight: '1.45',
                color: isUser ? '#f3f4f6' : '#cdd1dc',
                background: isUser ? 'rgba(212,164,23,0.12)' : SURFACE,
                border: `1px solid ${isUser ? 'rgba(212,164,23,0.35)' : BORDER}`,
              }}

              >
{isUser
  ? msg.text
  : <>
      <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }} />
      <button
        onClick={() => navigator.clipboard.writeText(msg.text)}
        style={{
          display: 'block', marginTop: '6px', fontSize: '10px',
          padding: '2px 8px', borderRadius: '4px', border: `1px solid ${BORDER}`,
          background: 'transparent', color: '#5b627a', cursor: 'pointer',
          fontFamily: 'sans-serif'
        }}
      >
        Copy
      </button>
    </>
}
{!isUser && msg.isReport && (
  <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
    <button
      onClick={() => downloadReport(msg.pdfBase64, msg.fullReport, msg.toolLabel || 'Report', msg.reportFilename)}
      style={{
        fontSize: '12px', padding: '6px 14px', borderRadius: '5px',
        border: '1px solid #D4A417', background: 'transparent',
        color: '#D4A417', cursor: 'pointer', fontFamily: 'sans-serif',
        display: 'flex', alignItems: 'center', gap: '6px'
      }}
    >
      ⬇ PDF
    </button>
    <button
      onClick={() => downloadDocx(msg.fullReport, msg.toolLabel || 'Report')}
      style={{
        fontSize: '12px', padding: '6px 14px', borderRadius: '5px',
        border: '1px solid #6b7db3', background: 'transparent',
        color: '#6b7db3', cursor: 'pointer', fontFamily: 'sans-serif'
      }}
    >
      ⬇ Word
    </button>
    <button
      onClick={clearMessages}
      style={{
        fontSize: '12px', padding: '6px 14px', borderRadius: '5px',
        border: '1px solid #252a38', background: 'transparent',
        color: '#7a8099', cursor: 'pointer', fontFamily: 'sans-serif'
      }}
    >
      ✕ New Analysis
    </button>
  </div>
)}
              </div>

              {/* Tool options card */}
              {!isUser && msg.isToolOptions && msg.toolOptions && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px', width: '85%' }}>
                  {msg.toolOptions.map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => selectToolOption(opt.value)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        textAlign: 'left', padding: '10px', borderRadius: '8px',
                        cursor: 'pointer', background: SURFACE,
                        border: `1px solid ${BORDER}`, fontFamily: 'sans-serif',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#e5e7eb' }}>
                          {opt.label}
                        </span>
                        <span style={{ display: 'block', fontSize: '11px', color: '#7a8099', marginTop: '2px' }}>
                          {opt.desc}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Tool follow-up (multi-select priorities) */}
              {!isUser && msg.isToolFollowUp && msg.followUpOptions && (
                <div style={{ marginTop: '10px', width: '85%' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                    {msg.followUpOptions.map(opt => {
                      const isSel = selectedPriorities.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setSelectedPriorities(prev =>
                            isSel ? prev.filter(v => v !== opt.value) : [...prev, opt.value]
                          )}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            textAlign: 'left', padding: '8px 10px', borderRadius: '6px',
                            cursor: 'pointer', background: isSel ? 'rgba(212,164,23,0.1)' : SURFACE,
                            border: `1px solid ${isSel ? GOLD : BORDER}`, fontFamily: 'sans-serif',
                          }}
                        >
                          <span style={{ fontSize: '12px', color: isSel ? GOLD : '#9ca3af' }}>
                            {isSel ? '☑' : '☐'}
                          </span>
                          <span style={{ fontSize: '13px', color: isSel ? GOLD : '#e5e7eb' }}>{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={runWithPriorities}
                      style={{
                        flex: 1, padding: '9px', borderRadius: '8px', border: 'none',
                        background: GOLD, color: '#13161e', fontSize: '13px',
                        fontWeight: '700', cursor: 'pointer', fontFamily: 'sans-serif'
                      }}
                    >
                      ▶ Run with Selected Priorities
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSelectedPriorities([]); runWithPriorities(); }}
                      style={{
                        padding: '9px 14px', borderRadius: '8px',
                        border: `1px solid ${BORDER}`, background: 'transparent',
                        color: '#7a8099', fontSize: '13px', cursor: 'pointer', fontFamily: 'sans-serif'
                      }}
                    >
                      Skip
                    </button>
                  </div>
                </div>
              )}

              {!isUser && msg.isDrawingCompareReady && (
                <div style={{ marginTop: '8px' }}>
                  {attachedFiles.length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#7a8099', padding: '8px', background: 'rgba(212,164,23,0.05)', borderRadius: '6px', border: '1px solid #252a38' }}>
                      📎 Attach the new drawing above, then click Run Comparison
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => runSelected()}
                      style={{
                        width: '100%', padding: '9px', borderRadius: '8px',
                        border: 'none', background: GOLD, color: '#13161e',
                        fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                        fontFamily: 'sans-serif'
                      }}
                    >
{pendingToolExecution?.toolId === 'track-changes' ? '▶ Run Track Changes' : 
 pendingToolExecution?.toolId === 'spec-check' ? '▶ Run Spec Check' :
 pendingToolExecution?.toolId === 'design-vs-shop' ? '▶ Run Design vs Shop' :
 '▶ Run Comparison'}
                    </button>
                  )}
                </div>
              )}

              {/* Chain suggestion card */}
              {!isUser && Array.isArray(msg.chainOptions) && msg.chainOptions.length > 0 && (
                <div
                  style={{
                    marginTop: '10px',
                    width: '85%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {msg.chainOptions.map((opt) => {
                    const chainLimit = getChainLimit(tier);
                    const isLocked = chainLimit === 0;
                    const isSel = selected.includes(opt.label);
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={isLocked ? undefined : () => toggleOption(opt.label)}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          textAlign: 'left',
                          padding: '10px',
                          borderRadius: '8px',
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                          background: SURFACE,
                          border: `1px solid ${isLocked ? '#3d4460' : (isSel ? GOLD : BORDER)}`,
                          fontFamily: 'sans-serif',
                          opacity: isLocked ? 0.5 : 1,
                        }}
                      >
                        <span style={{ fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>{opt.icon}</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{
                              display: 'block',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: isSel ? GOLD : '#e5e7eb',
                            }}
                          >
                            {opt.label}
                          </span>
                          <span style={{ display: 'block', fontSize: '11px', color: '#7a8099', marginTop: '2px' }}>
                            {opt.desc}
                          </span>
                        </span>
                      </button>
                    );
                  })}

                  {(() => {
                    const NO_FILE_TOOLS = ['draft-rfi', 'submittal-review-letter'];
                    const allSelectedNeedNoFiles = selected.every(label => {
                      const entry = Object.entries(TOOL_REGISTRY).find(([, t]) => t.label === label);
                      return entry && NO_FILE_TOOLS.includes(entry[0]);
                    });
                    return selected.length > 0 && attachedFiles.length === 0 && lastUsedFilesRef.current.length > 0 && !allSelectedNeedNoFiles && (
                      <div style={{ fontSize: '11px', color: '#D4A417', marginTop: '4px', padding: '4px 8px', background: 'rgba(212,164,23,0.08)', borderRadius: '4px' }}>
                        📎 Attach the required file above, then click Run Selected
                      </div>
                    );
                  })()}

                  {getChainLimit(tier) === 0 ? (
                    <div style={{ fontSize: '11px', color: '#7a8099', padding: '8px', textAlign: 'center', marginTop: '4px' }}>
                      🔒 Chaining requires Pro or Enterprise. Contact hello@aecworks.ai to upgrade.
                    </div>
                  ) : selected.length > 0 && (
                    <button
                      type="button"
                      onClick={runSelected}
                      disabled={loading}
                      style={{
                        marginTop: '2px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        background: loading ? '#6b5a0a' : GOLD,
                        color: '#13161e',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontFamily: 'sans-serif',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        opacity: loading ? 0.6 : 1,
                      }}
                    >
                      ▶ Run Selected Checks
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Input area (pinned to bottom) */}
      <div style={{ borderTop: `1px solid ${BORDER}`, padding: '12px', flexShrink: 0 }}>
        {/* File drop zone — click to browse via the hidden input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.target.files);
            setAttachedFiles((prev) => [...prev, ...files]);
            e.target.value = ''; // reset so same file can be re-selected
          }}
        />
        <div
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files);
            setAttachedFiles((prev) => [...prev, ...files]);
          }}
          style={{
            border: `1px dashed ${BORDER}`,
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'center',
            fontSize: '12px',
            color: '#7a8099',
            marginBottom: '10px',
            cursor: 'pointer',
          }}
        >
{activeTab === 'spec-check' 
  ? '📎 Drop files or attach — up to 30 specs, 20 drawings, 1 submittal'
  : activeTab === 'shop-drawing-check'
  ? '📎 Drop files or attach — up to 20 shop drawing files'
  : activeTab === 'design-vs-shop'
  ? '📎 Drop files or attach — up to 20 design drawings + up to 20 shop drawings'
  : activeTab === 'drawing-compare'
  ? '📎 Drop files or attach — add baseline drawing set first then the new drawing set will be requested'
  : activeTab === 'rfi-check'
  ? '📎 Drop files or attach — RFI document + project docs (up to 10 drawings)'
  : activeTab === 'spec-builder'
  ? '📎 Drop files or attach — master spec + optional template'
  : activeTab === 'spec-update'
  ? '📎 Drop files or attach — spec sheet to update'
  : '📎 Drop files or attach to message'
}
        </div>

        <button
          type="button"
          onClick={() => setShowPlatformPicker(true)}
          style={{
            fontSize: '11px',
            padding: '5px 10px',
            borderRadius: '5px',
            border: `1px solid ${BORDER}`,
            background: 'transparent',
            color: '#7a8099',
            cursor: 'pointer',
            fontFamily: 'sans-serif',
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          📁 Platform Files
        </button>

        {/* Attached file chips */}
        {attachedFiles.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {attachedFiles.map((f, i) => (
              <div key={i} style={{ fontSize: '11px', padding: '3px 8px', background: '#252a38', border: '1px solid #2e3548', borderRadius: '4px', color: '#e8eaf0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📄 {f.name.length > 20 ? f.name.slice(0, 20) + '...' : f.name}
                <span style={{ cursor: 'pointer', color: '#7a8099' }} onClick={() => setAttachedFiles((prev) => prev.filter((_, j) => j !== i))}>✕</span>
              </div>
            ))}
          </div>
        )}

        {/* Text input + send */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message AW Manager…"
            rows={2}
            style={{
              flex: 1,
              minWidth: 0,
              padding: '9px 12px',
              borderRadius: '8px',
              border: `1px solid ${BORDER}`,
              background: SURFACE,
              color: '#f3f4f6',
              fontSize: '13px',
              fontFamily: 'sans-serif',
              outline: 'none',
              resize: 'none',
              lineHeight: '1.4',
              minHeight: '42px',
              maxHeight: '120px',
              overflowY: 'auto',
            }}
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={loading}
            style={{
              padding: '0 16px',
              borderRadius: '8px',
              border: `1px solid ${GOLD}`,
              background: 'transparent',
              color: GOLD,
              fontSize: '13px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'sans-serif',
              opacity: loading ? 0.5 : 1,
            }}
          >
            Send
          </button>
        </div>

        {/* Context chips — one per cached doc */}
{(docs.length > 0 || vaultDrawings?.length > 0) && (
  <div style={{ 
    display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px',
    maxHeight: '80px', overflowY: 'auto',
    scrollbarWidth: 'thin', scrollbarColor: '#252a38 transparent'
  }}>
            {docs.map((doc, i) => (
              <span
                key={(doc && doc.name) ? `${doc.name}-${i}` : i}
                title={`Click to attach: ${doc.name}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 9px',
                  borderRadius: '12px',
                  background: SURFACE,
                  border: `1px solid ${BORDER}`,
                  fontSize: '11px',
                  color: '#9ca3af',
                  opacity: fetchingDocs.has(doc.id) ? 0.6 : 1,
                  cursor: fetchingDocs.has(doc.id) ? 'wait' : 'pointer',
                }}
              >
                <span
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: dotColorForDocType(doc && doc.docType),
                    flexShrink: 0,
                  }}
                  onClick={() => fetchAndAttachCachedDoc(doc)}
                />
                <span onClick={() => fetchAndAttachCachedDoc(doc)}>
                  {truncate((doc && doc.name) || 'Untitled', 16)}
                </span>
                <span
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Delete "${doc.name}" from cache?`)) return;
                    try {
                      await fetch(`${API_BASE}/api/documents/${doc.id}?userId=${encodeURIComponent(user?.email || '')}`, { method: 'DELETE' });
                      onCacheChange?.();
                    } catch (err) {
                      console.error('Delete failed:', err);
                    }
                  }}
                  style={{ color: '#7a8099', cursor: 'pointer', fontSize: '10px', marginLeft: '2px' }}
                >
                  ✕
                </span>
              </span>
            ))}
            {vaultDrawings?.length > 0 && vaultDrawings.map((d, i) => (
              <span
                key={d.id || i}
                title={`Click to attach: ${d.fileName}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '3px 9px', borderRadius: '12px',
                  background: 'rgba(59,130,246,0.1)',
                  border: `1px solid ${fetchingDocs.has(d.id) ? '#3b82f6' : 'rgba(59,130,246,0.3)'}`,
                  fontSize: '11px', color: '#60a5fa',
                  opacity: fetchingDocs.has(d.id) ? 0.6 : 1,
                  cursor: fetchingDocs.has(d.id) ? 'wait' : 'pointer',
                }}
              >
                <span
                  style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }}
                  onClick={() => fetchAndAttachVaultDrawing(d)}
                />
                <span onClick={() => fetchAndAttachVaultDrawing(d)}>
                  {truncate(d.fileName, 16)}
                </span>
                <span
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Delete "${d.fileName}" from vault?`)) return;
                    try {
                      await fetch(`${API_BASE}/api/drawing-compare/vault/${d.id}?userId=${encodeURIComponent(user?.email || '')}`, { method: 'DELETE' });
                      setMessages(prev => [...prev, { role: 'agent', text: `🗑️ Deleted "${d.fileName}" from vault.` }]);
                      onVaultChange?.();
                    } catch (err) {
                      console.error('Delete failed:', err);
                    }
                  }}
                  style={{ color: '#7a8099', cursor: 'pointer', fontSize: '10px', marginLeft: '2px' }}
                >
                  ✕
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {showPlatformPicker && (
        <PlatformFilePicker
          user={user}
          onFileSelect={(file) => {
            setAttachedFiles(prev => {
              const next = [...prev, file];
              attachedFilesRef.current = next;
              return next;
            });
            setShowPlatformPicker(false);
          }}
          onClose={() => setShowPlatformPicker(false)}
        />
      )}
    </div>
  );
}

export default ManagerSidebar;
